import { type FC, useCallback, useMemo } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import {
  LOCATION_OPTIONS,
  parseLocationTypeKind,
} from '@/lib/crm/utils/locationType';

import type { PropertyInputProps } from './TextInput';

/**
 * Location type selector — emits plain strings ('google_meet', 'zoom', etc.)
 * matching the format expected by the scheduling backend.
 *
 * Custom location details are stored in the separate `ahoy_custom_location`
 * property and should be edited as a distinct form field.
 */
export const LocationTypeInput: FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  size = 'sm',
}) => {
  const selected = useMemo(() => parseLocationTypeKind(value), [value]);

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      if (key !== null && key !== undefined) {
        onChange(String(key));
      }
    },
    [onChange]
  );

  return (
    <Select
      size={size}
      items={LOCATION_OPTIONS}
      selectedKey={selected}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      aria-label={propertyType.label || 'Location type'}
    >
      {/* PATTERN-EXCEPTION(audit-2026-04-30): Select render-prop not wrapped in
          useCallback — this is a pervasive pattern for Select children across the
          codebase. LOCATION_OPTIONS is module-scope so the closure has no deps,
          but fixing in isolation would be inconsistent without a codebase-wide
          Select render-prop convention. See docs/audit-reports/2026-04-30-booking-link-slideout.md. */}
      {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
    </Select>
  );
};
